/**
 * Shapes of the learner progress endpoints whose JSON the OpenAPI schema leaves open (`unknown`):
 * progress (evolution, rank, analysis), daily challenge, exam centres, structure.
 * Read defensively, like types.ts: a missing field never crashes a screen.
 */

import { parsePicture, parsePictures, speakingSeconds } from "@agencyos/core";

import { SKILLS, type Skill, type StateQuestion, isObject, isSkill, num, str } from "./types";

export interface SkillPoint {
  score: number | null;
  cefr: string;
  nclc: number | null;
}

export interface EvolutionPoint {
  attempt: string;
  kind: string;
  mock: number | null;
  at: string;
  skills: Partial<Record<Skill, SkillPoint>>;
}

export interface RankBlock {
  position: number;
  total: number;
  topPercent: number;
}

export type Trend = "up" | "down" | "stable" | "unknown";
export type Verdict = "ready" | "almost" | "not_ready";

export interface SkillAnalysis {
  nclc: number | null;
  gap: number | null;
  trend: Trend;
}

/** One critical point: the code picks the text, the other fields fill it. */
export interface CriticalPoint {
  code: string;
  skill: Skill | null;
  gap: number | null;
  level: string;
  success: number | null;
  criterion: string;
  average: number | null;
  count: number | null;
  daysLeft: number | null;
  weeksNeeded: number | null;
  message: string;
}

export interface Analysis {
  verdict: Verdict;
  target: number;
  skills: Record<Skill, SkillAnalysis>;
  critical: CriticalPoint[];
  daysLeft: number | null;
}

export interface Progress {
  evolution: EvolutionPoint[];
  platform: RankBlock | null;
  agency: RankBlock | null;
  analysis: Analysis;
  xp: number;
  streakDays: number;
}

const numOrNull = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

function parseRank(value: unknown): RankBlock | null {
  if (!isObject(value) || typeof value.position !== "number") return null;
  return { position: num(value.position), total: num(value.total), topPercent: num(value.top_percent, 100) };
}

function parseTrend(value: unknown): Trend {
  return value === "up" || value === "down" || value === "stable" ? value : "unknown";
}

function parseVerdict(value: unknown): Verdict {
  return value === "ready" || value === "almost" ? value : "not_ready";
}

export function parseEvolution(value: unknown): EvolutionPoint[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isObject).map((row) => {
    const skills: Partial<Record<Skill, SkillPoint>> = {};
    const raw = isObject(row.skills) ? row.skills : {};
    for (const skill of SKILLS) {
      const point = raw[skill];
      if (!isObject(point)) continue;
      skills[skill] = { score: numOrNull(point.score), cefr: str(point.cefr), nclc: numOrNull(point.nclc) };
    }
    return {
      attempt: str(row.attempt),
      kind: str(row.kind),
      mock: numOrNull(row.mock),
      at: str(row.at),
      skills,
    };
  });
}

function parseCritical(value: unknown): CriticalPoint {
  const row = isObject(value) ? value : {};
  return {
    code: str(row.code),
    skill: isSkill(row.skill) ? row.skill : null,
    gap: numOrNull(row.gap),
    level: str(row.level),
    success: numOrNull(row.success),
    criterion: str(row.criterion),
    average: numOrNull(row.average),
    count: numOrNull(row.count),
    daysLeft: numOrNull(row.days_left),
    weeksNeeded: numOrNull(row.weeks_needed),
    message: str(row.message),
  };
}

export function parseAnalysis(value: unknown): Analysis {
  const data = isObject(value) ? value : {};
  const raw = isObject(data.skills) ? data.skills : {};
  const skills = {} as Record<Skill, SkillAnalysis>;
  for (const skill of SKILLS) {
    const row = isObject(raw[skill]) ? (raw[skill] as Record<string, unknown>) : {};
    skills[skill] = { nclc: numOrNull(row.nclc), gap: numOrNull(row.gap), trend: parseTrend(row.trend) };
  }
  return {
    verdict: parseVerdict(data.verdict),
    target: num(data.target, 7),
    skills,
    critical: Array.isArray(data.critical) ? data.critical.map(parseCritical) : [],
    daysLeft: numOrNull(data.days_left),
  };
}

export function parseProgress(value: {
  evolution: unknown;
  rank: unknown;
  analysis: unknown;
  xp: number;
  streak_days: number;
}): Progress {
  const rank = isObject(value.rank) ? value.rank : {};
  return {
    evolution: parseEvolution(value.evolution),
    platform: parseRank(rank.platform),
    agency: parseRank(rank.agency),
    analysis: parseAnalysis(value.analysis),
    xp: num(value.xp),
    streakDays: num(value.streak_days),
  };
}

// --- Daily challenge -------------------------------------------------------------------------

export interface ChallengeEntry {
  startedAt: string;
  finished: boolean;
  score: number;
  seconds: number;
  total: number;
}

export interface LeaderRow {
  position: number;
  name: string;
  score: number;
  seconds: number;
  me: boolean;
}

export interface Leaderboard {
  top: LeaderRow[];
  myPosition: number | null;
  participants: number;
}

export function parseChallengeQuestions(value: unknown): StateQuestion[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isObject).map((q) => ({
    id: str(q.id),
    index: num(q.index),
    level: str(q.level),
    kind: str(q.kind),
    document: str(q.document),
    script_for_speech: [],
    has_audio: q.has_audio === true,
    stem: str(q.stem),
    options: Array.isArray(q.options) ? q.options.map((o) => str(o)) : [],
    flow: null,
    image: parsePicture(q.image),
    option_images: parsePictures(q.option_images),
  }));
}

export function parseEntry(value: unknown): ChallengeEntry | null {
  if (!isObject(value)) return null;
  return {
    startedAt: str(value.started_at),
    finished: value.finished === true,
    score: num(value.score),
    seconds: num(value.seconds),
    total: num(value.total),
  };
}

export function parseLeaderboard(value: unknown): Leaderboard {
  const data = isObject(value) ? value : {};
  return {
    top: (Array.isArray(data.top) ? data.top : []).filter(isObject).map((row) => ({
      position: num(row.position),
      name: str(row.name),
      score: num(row.score),
      seconds: num(row.seconds),
      me: row.me === true,
    })),
    myPosition: numOrNull(data.my_position),
    participants: num(data.participants),
  };
}

// --- Exam centres and exam structure -------------------------------------------------------

export interface ExamCenter {
  code: string;
  city: string;
  name: string;
}

export function parseCenters(value: unknown): ExamCenter[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(isObject)
    .map((c) => ({ code: str(c.code), city: str(c.city), name: str(c.name) }))
    .filter((c) => c.city);
}

export interface SectionShape {
  /** CO/CE: number of questions; EE/EO: number of tasks. */
  count: number;
  /** Duration of the section in minutes (EO: preparation + speaking time, rounded up). */
  minutes: number;
}

/** Official structure of the exam read from `academy/format` (versioned table, never in code). */
export function parseStructure(sections: unknown): Partial<Record<Skill, SectionShape>> {
  const out: Partial<Record<Skill, SectionShape>> = {};
  if (!isObject(sections)) return out;
  for (const skill of SKILLS) {
    const row = sections[skill];
    if (!isObject(row)) continue;
    const tasks: Record<string, { prep_seconds?: number; seconds?: number }> = {};
    if (isObject(row.tasks)) {
      for (const [key, task] of Object.entries(row.tasks)) {
        tasks[key] = isObject(task)
          ? { prep_seconds: numOrNull(task.prep_seconds) ?? 0, seconds: numOrNull(task.seconds) ?? 0 }
          : {};
      }
    }
    const count = skill === "co" || skill === "ce" ? num(row.questions) : Object.keys(tasks).length;
    const minutes =
      typeof row.minutes === "number" ? row.minutes : Math.ceil(speakingSeconds(tasks) / 60);
    out[skill] = { count, minutes };
  }
  return out;
}

/** Best sitting of a numbered mock (`best` of academy/mocks). */
export interface MockBest {
  attempt: string;
  total: number;
  nclc: Partial<Record<Skill, number>>;
}

export function parseBest(value: unknown): MockBest | null {
  if (!isObject(value)) return null;
  const nclc: Partial<Record<Skill, number>> = {};
  if (isObject(value.nclc)) {
    for (const skill of SKILLS) {
      const n = numOrNull(value.nclc[skill]);
      if (n !== null) nclc[skill] = n;
    }
  }
  return { attempt: str(value.attempt), total: num(value.total), nclc };
}
